const GAPI_SRC = 'https://apis.google.com/js/api.js';
const GIS_SRC = 'https://accounts.google.com/gsi/client';
const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const TOKEN_REUSE_MARGIN_MS = 60_000;

/** The Picker's browser settings, handed out by the upload intent. */
export interface PickerSettings {
  pickerClientId: string;
  pickerApiKey: string;
  pickerAppId: string;
}

export interface PickedFile {
  id: string;
  name?: string;
  mimeType?: string;
}

export type PickerOutcome = { kind: 'uploaded'; files: PickedFile[] } | { kind: 'cancelled' };

export class DrivePickerError extends Error {
  constructor(
    readonly reason: 'not-configured' | 'script' | 'sign-in' | 'popup',
    message: string,
  ) {
    super(message);
  }
}

interface PickerResponse {
  action: string;
  docs?: PickedFile[];
}

interface PickerInstance {
  setVisible(visible: boolean): void;
  dispose(): void;
}

interface PickerBuilder {
  setAppId(appId: string): PickerBuilder;
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setLocale(locale: string): PickerBuilder;
  setTitle(title: string): PickerBuilder;
  addView(view: unknown): PickerBuilder;
  enableFeature(feature: string): PickerBuilder;
  setCallback(callback: (response: PickerResponse) => void): PickerBuilder;
  build(): PickerInstance;
}

interface PickerNamespace {
  PickerBuilder: new () => PickerBuilder;
  DocsUploadView: new () => { setParent(folderId: string): unknown };
  Feature: { MULTISELECT_ENABLED: string };
  Action: { PICKED: string; CANCEL: string; ERROR?: string };
}

interface TokenResponse {
  access_token?: string;
  expires_in?: number | string;
  error?: string;
}

interface TokenClient {
  requestAccessToken(overrides?: { prompt?: string }): void;
}

/** Above MUI's modal, snackbar and tooltip layers, so the Picker opens on top of a dialog that launched it. */
const PICKER_LAYER_CSS =
  '.picker-dialog-bg{z-index:20000!important}.picker-dialog{z-index:20001!important}';

interface TokenClientConfig {
  client_id: string;
  scope: string;
  login_hint?: string;
  callback: (response: TokenResponse) => void;
  error_callback?: (error: { type?: string; message?: string }) => void;
}

interface GoogleGlobals {
  gapi?: { load(api: string, options: { callback: () => void; onerror: () => void }): void };
  google?: {
    picker?: PickerNamespace;
    accounts?: { oauth2?: { initTokenClient(config: TokenClientConfig): TokenClient } };
  };
}

const globals = (): GoogleGlobals => window as unknown as GoogleGlobals;

const scripts = new Map<string, Promise<void>>();

const loadScript = (src: string): Promise<void> => {
  const cached = scripts.get(src);
  if (cached) return cached;
  const loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scripts.delete(src);
      script.remove();
      reject(new DrivePickerError('script', `Could not load ${src}`));
    };
    document.head.appendChild(script);
  });
  scripts.set(src, loading);

  return loading;
};

let pickerReady: Promise<PickerNamespace> | undefined;

const loadPicker = (): Promise<PickerNamespace> => {
  pickerReady ??= loadScript(GAPI_SRC)
    .then(
      () =>
        new Promise<PickerNamespace>((resolve, reject) => {
          const gapi = globals().gapi;
          if (!gapi) {
            reject(new DrivePickerError('script', 'Google API loader missing'));

            return;
          }
          gapi.load('picker', {
            callback: () => {
              const picker = globals().google?.picker;
              if (picker) resolve(picker);
              else reject(new DrivePickerError('script', 'Google Picker missing'));
            },
            onerror: () => reject(new DrivePickerError('script', 'Google Picker failed to load')),
          });
        }),
    )
    .catch((error: unknown) => {
      pickerReady = undefined;
      throw error;
    });

  return pickerReady;
};

/** Starts loading both of Google's scripts, so the click that opens the Picker does not wait for them. */
export const preloadDrivePicker = (): void => {
  loadPicker().catch(() => undefined);
  loadScript(GIS_SRC).catch(() => undefined);
};

let cachedToken:
  | { clientId: string; loginHint?: string; token: string; expiresAt: number }
  | undefined;
let rememberedSettings: PickerSettings | undefined;
let chooseAccountNext = false;

/** Keeps the environment's Picker settings, so a later click can start Google's sign-in before the server answers. */
export const rememberPickerSettings = (settings: PickerSettings): void => {
  rememberedSettings = settings;
};

const liveToken = (clientId: string, loginHint: string | undefined): string | undefined =>
  cachedToken &&
  cachedToken.clientId === clientId &&
  cachedToken.loginHint === loginHint &&
  cachedToken.expiresAt - TOKEN_REUSE_MARGIN_MS > Date.now()
    ? cachedToken.token
    : undefined;

/** A Picker closed without an upload may have been the wrong Google account: forget the token and offer the choice. */
const forgetToken = () => {
  cachedToken = undefined;
  chooseAccountNext = true;
};

/**
 * The person's own `drive.file` token. Google opens its sign-in window only shortly after a click, so call this from the
 * click itself whenever possible — every step before `requestAccessToken` is synchronous once Google's script is loaded.
 */
export const requestDriveToken = (
  clientId: string,
  loginHint: string | undefined,
): Promise<string> => {
  if (!clientId) {
    return Promise.reject(
      new DrivePickerError(
        'not-configured',
        'Google Picker is not configured for this environment',
      ),
    );
  }
  const live = liveToken(clientId, loginHint);
  if (live) return Promise.resolve(live);
  const oauth2 = globals().google?.accounts?.oauth2;
  if (!oauth2) {
    return loadScript(GIS_SRC).then(() => {
      if (!globals().google?.accounts?.oauth2) {
        throw new DrivePickerError('script', 'Google sign-in missing');
      }

      return requestDriveToken(clientId, loginHint);
    });
  }

  return new Promise<string>((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_FILE_SCOPE,
      login_hint: loginHint,
      callback: (response) => {
        if (!response.access_token) {
          reject(new DrivePickerError('sign-in', response.error ?? 'Google sign-in refused'));

          return;
        }
        const lifetimeSeconds = Number(response.expires_in ?? 0) || 0;
        cachedToken = {
          clientId,
          loginHint,
          token: response.access_token,
          expiresAt: Date.now() + lifetimeSeconds * 1000,
        };
        chooseAccountNext = false;
        resolve(response.access_token);
      },
      error_callback: (error) =>
        reject(
          new DrivePickerError(
            error.type === 'popup_failed_to_open' ? 'popup' : 'sign-in',
            error.message ?? error.type ?? 'Google sign-in closed',
          ),
        ),
    });
    client.requestAccessToken({ prompt: chooseAccountNext ? 'select_account' : '' });
  });
};

/**
 * Starts Google's sign-in inside the click that asked for an upload, with the settings an earlier upload intent handed
 * out; undefined before any intent was seen this session, or when no sign-in is needed.
 */
export const beginDriveSignIn = (loginHint: string | undefined): Promise<string> | undefined => {
  if (!rememberedSettings) return undefined;
  const started = requestDriveToken(rememberedSettings.pickerClientId, loginHint);
  started.catch(() => undefined);

  return started;
};

let layerStyled = false;

const raisePickerLayer = () => {
  if (layerStyled) return;
  const style = document.createElement('style');
  style.setAttribute('data-drive-picker-layer', '');
  style.textContent = PICKER_LAYER_CSS;
  document.head.appendChild(style);
  layerStyled = true;
};

export interface OpenPicker {
  outcome: Promise<PickerOutcome>;
  /** Closes the Picker, as when the screen that opened it goes away; the outcome becomes cancelled. */
  close: () => void;
}

/**
 * Opens Google's Picker on one folder, uploading as the person's own Google account with the `drive.file` scope only:
 * the platform never sees the files or the token. The folder is the one the upload intent resolved — never one the
 * person can browse to — so a file lands exactly where the platform expects it.
 */
export const showUploadPicker = async ({
  folderId,
  settings,
  token,
  locale,
  title,
}: {
  folderId: string;
  settings: PickerSettings;
  token: string;
  locale?: string;
  title?: string;
}): Promise<OpenPicker> => {
  if (!settings.pickerApiKey || !settings.pickerAppId) {
    throw new DrivePickerError(
      'not-configured',
      'Google Picker is not configured for this environment',
    );
  }
  const picker = await loadPicker();
  raisePickerLayer();
  let settle: (outcome: PickerOutcome) => void = () => undefined;
  let fail: (error: DrivePickerError) => void = () => undefined;
  const outcome = new Promise<PickerOutcome>((resolve, reject) => {
    settle = resolve;
    fail = reject;
  });
  const uploadView = new picker.DocsUploadView();
  uploadView.setParent(folderId);
  let builder = new picker.PickerBuilder()
    .setAppId(settings.pickerAppId)
    .setOAuthToken(token)
    .setDeveloperKey(settings.pickerApiKey)
    .addView(uploadView)
    .enableFeature(picker.Feature.MULTISELECT_ENABLED);
  if (locale) builder = builder.setLocale(locale);
  if (title) builder = builder.setTitle(title);
  let open = true;
  const finish = (then: () => void) => {
    if (!open) return;
    open = false;
    instance.dispose();
    then();
  };
  const instance = builder
    .setCallback((response) => {
      if (response.action === picker.Action.PICKED) {
        finish(() => settle({ kind: 'uploaded', files: response.docs ?? [] }));
      } else if (response.action === picker.Action.CANCEL) {
        finish(() => {
          forgetToken();
          settle({ kind: 'cancelled' });
        });
      } else if (picker.Action.ERROR && response.action === picker.Action.ERROR) {
        finish(() => {
          forgetToken();
          fail(new DrivePickerError('script', 'Google Picker reported an error'));
        });
      }
    })
    .build();
  instance.setVisible(true);

  return { outcome, close: () => finish(() => settle({ kind: 'cancelled' })) };
};
